import { useEffect, useState } from 'react';
import { Empty, Spin, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  broadcastListMine,
  broadcastMarkRead,
  type MyBroadcastItem,
  type BroadcastPriority,
} from '@/services/broadcast';
import styles from './BroadcastTab.module.css';

const PRIORITY_TAG: Record<BroadcastPriority, { color: string; label: string }> = {
  normal:    { color: 'blue',   label: '普通' },
  important: { color: 'orange', label: '重要' },
  critical:  { color: 'red',    label: '紧急' },
};

function BroadcastTab() {
  const [items, setItems] = useState<MyBroadcastItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<MyBroadcastItem | null>(null);

  useEffect(() => {
    broadcastListMine()
      .then(setItems)
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = async (item: MyBroadcastItem) => {
    setActive(item);
    if (!item.readAt) {
      try {
        await broadcastMarkRead(item.broadcast.id);
        setItems((prev) =>
          prev.map((i) =>
            i.broadcast.id === item.broadcast.id
              ? { ...i, readAt: new Date().toISOString() }
              : i,
          ),
        );
      } catch { /* ignore */ }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {active && (
          <button className={styles.backBtn} onClick={() => setActive(null)}>
            <ArrowLeftOutlined /> 返回
          </button>
        )}
        <span className={styles.headerTitle}>
          {active ? active.broadcast.title : '广播信箱'}
        </span>
      </div>

      <div className={styles.body}>
        {loading ? (
          <div className={styles.centered}><Spin /></div>
        ) : active ? (
          <div className={styles.detail}>
            <div className={styles.detailMeta}>
              <Tag color={PRIORITY_TAG[active.broadcast.priority].color}>
                {PRIORITY_TAG[active.broadcast.priority].label}
              </Tag>
              <span className={styles.detailDate}>
                {dayjs(active.broadcast.createdAt).format('YYYY-MM-DD HH:mm')}
              </span>
              {active.broadcast.senderName && (
                <span className={styles.detailSender}>
                  · 发自 {active.broadcast.senderName}
                </span>
              )}
            </div>
            <div className={styles.detailContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {active.broadcast.contentMd}
              </ReactMarkdown>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className={styles.centered}>
            <Empty
              description={
                <span style={{ color: 'rgba(255,255,255,0.35)' }}>暂无广播</span>
              }
            />
          </div>
        ) : (
          items.map((it) => (
            <div
              key={it.broadcast.id}
              className={`${styles.item} ${!it.readAt ? styles.itemUnread : ''}`}
              onClick={() => handleSelect(it)}
            >
              <div className={styles.itemTop}>
                <span className={styles.itemTitle}>{it.broadcast.title}</span>
                <Tag
                  color={PRIORITY_TAG[it.broadcast.priority].color}
                  style={{ marginInlineEnd: 0 }}
                >
                  {PRIORITY_TAG[it.broadcast.priority].label}
                </Tag>
              </div>
              <div className={styles.itemMeta}>
                {dayjs(it.broadcast.createdAt).format('YYYY-MM-DD HH:mm')}
                {!it.readAt && <span className={styles.unreadDot}>● 未读</span>}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default BroadcastTab;
